import { useCallback, useMemo } from 'react';
import { usePermission } from '../../hooks/usePermission';
import { useGlobalStore } from '../../stores/globalStore';
import { useFeatureVisibilityStore } from '../../stores/featureVisibilityStore';
import { useFacilitySettings } from '../../api/selectiveTouchApi';
import { usePurchasingLicensed } from '../../api/licensesApi';
import { useAssessmentsOff } from '../clinical/assessmentsOff';
import { shownByFeatureAccess } from '../../navigation/navConfig';
import {
  DASHBOARD_ACTIONS,
  DEFAULT_ACTION_IDS,
  findAction,
  type DashboardActionDef,
} from './dashboardActions';

export interface ResolvedAction extends DashboardActionDef {
  /** Label with the facility's own wording substituted in. */
  displayLabel: string;
}

/**
 * Which quick actions this user may see, and which they have pinned.
 *
 * Availability is recomputed on every render rather than trusted from the
 * saved row. A saved action is a *preference*, not a grant: someone's security
 * level can be reduced, or a facility can switch KDS off, long after the
 * button was pinned — and a stale pin would otherwise put a button on screen
 * that 403s when pressed.
 *
 * Three gates, matching the ones the sidebar already applies:
 *   facility feature flag  →  whole site      (kDS_Enabled, UseAssessment)
 *   security level         →  per user        (dietech_*_Level)
 *   feature visibility     →  per user, self-chosen
 */
export function useQuickActions(savedIds: string[] | null | undefined) {
  const { getLevel } = usePermission();
  const facilityId = useGlobalStore((s) => s.facilityId);
  const residentPluralLabel = useGlobalStore((s) => s.residentPluralLabel);
  const isVisible = useFeatureVisibilityStore((s) => s.isVisible);
  const { data: facilitySettings } = useFacilitySettings(facilityId);
  const kdsEnabled = !!facilitySettings?.kDS_Enabled;
  const assessmentsOff = useAssessmentsOff();
  const purchasingLicensed = usePurchasingLicensed();

  const isAvailable = useCallback(
    (a: DashboardActionDef) => {
      if (a.requiresKds && !kdsEnabled) return false;
      if (a.requiresAssessments && assessmentsOff) return false;
      // Purchasing is a paid module: no button for a facility not licensed for it in Tech Log.
      if (a.route?.startsWith('/ordering') && !purchasingLicensed) return false;
      if (a.securityField && getLevel(a.securityField) < 1) return false;
      // A page (or a whole section) the user has hidden from their own menu should not come
      // back as a dashboard button.
      if (a.route && !shownByFeatureAccess(a.route, facilityId, isVisible)) return false;
      return true;
    },
    [kdsEnabled, assessmentsOff, purchasingLicensed, getLevel, isVisible, facilityId],
  );

  const resolve = useCallback(
    (a: DashboardActionDef): ResolvedAction => ({
      ...a,
      displayLabel: a.usesResidentLabel ? residentPluralLabel : a.label,
    }),
    [residentPluralLabel],
  );

  /** Everything this user could pin, for the picker. */
  const available = useMemo(
    () => DASHBOARD_ACTIONS.filter(isAvailable).map(resolve),
    [isAvailable, resolve],
  );

  /**
   * What is actually on the bar.
   *
   * `null` means the user has never chosen, so they get the defaults. An empty
   * array means they chose to have none, and that is respected — the two are
   * deliberately different states.
   */
  const pinned = useMemo(() => {
    const ids = savedIds ?? DEFAULT_ACTION_IDS;
    return ids
      .map(findAction)
      .filter((a): a is DashboardActionDef => Boolean(a))
      .filter(isAvailable)
      .map(resolve);
  }, [savedIds, isAvailable, resolve]);

  return { pinned, available };
}
